'use client'

import { useCallback, useState } from 'react'

import { XIcon } from '@phosphor-icons/react'
import { Mousewheel } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper/types'

import 'swiper/css'

import { Button } from '@/components/ui/button'
import { useLegacyDevice } from '@/hooks/utils'
import { useRouter } from '@/i18n/navigation'
import { CreationDetail } from '@/lib/api'

import { LegacyMobileWork } from './legacy-mobile-work'
import { WorkVideoCard } from './work-video-card'

const RENDER_WINDOW_SIZE = 1

export type MobileWorkProps = {
  list: CreationDetail[]
  initialIndex: number
  data?: CreationDetail
}

export const MobileWork = ({ list, initialIndex }: MobileWorkProps) => {
  const router = useRouter()
  const isLegacyDevice = useLegacyDevice()
  const [activeIndex, setActiveIndex] = useState(() => initialIndex)

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      const newIndex = swiper.activeIndex
      setActiveIndex(newIndex)

      // 延迟更新路由，避免滑动过程中的闪动
      const currentVideo = list[newIndex]
      if (currentVideo) {
        const videoId = currentVideo.jobId || currentVideo.id.toString()
        const locale = window.location.pathname.split('/')[1]
        const newUrl = `/${locale}/work/${videoId}`

        // 使用原生 History API 更新 URL，不触发路由重新渲染
        window.history.replaceState(
          { ...window.history.state, as: newUrl, url: newUrl },
          '',
          newUrl
        )
      }
    },
    [list]
  )

  const handleClose = useCallback(() => {
    router.back()
  }, [router])

  const handleSwiper = useCallback((swiper: SwiperType) => {
    console.log('swiper', swiper)
  }, [])

  const handleReachEnd = useCallback(() => {
    console.log('reach end')
  }, [])

  // 低版本设备使用原生滚动
  if (isLegacyDevice) {
    return <LegacyMobileWork initialIndex={initialIndex} list={list} />
  }

  return (
    <div className="relative h-[100dvh] w-screen bg-black">
      {/* 关闭按钮 */}
      <div className="absolute top-4 left-4 z-30">
        <Button
          className="bg-glass h-10 w-10 rounded-full p-0"
          size="icon"
          variant="ghost"
          onClick={handleClose}
        >
          <XIcon className="text-white" size={24} />
        </Button>
      </div>

      {/* Swiper容器 - 抖音式垂直滑动 */}
      <Swiper
        allowTouchMove
        followFinger
        longSwipes
        shortSwipes
        className="swiper-vertical !h-full w-full"
        cssMode={false}
        direction="vertical"
        freeMode={false}
        initialSlide={initialIndex}
        longSwipesMs={10}
        longSwipesRatio={0.25}
        modules={[Mousewheel]}
        resistanceRatio={0.65}
        slidesPerView={1}
        speed={350}
        threshold={2}
        touchAngle={45}
        touchRatio={1.5}
        mousewheel={{
          forceToAxis: true,
          sensitivity: 1,
          thresholdDelta: 10
        }}
        onReachEnd={handleReachEnd}
        onSlideChange={handleSlideChange}
        onSwiper={handleSwiper}
      >
        {list.map((item, index) => (
          <SwiperSlide key={item.id} style={{ height: '100%' }}>
            {Math.abs(index - activeIndex) <= RENDER_WINDOW_SIZE ? (
              <WorkVideoCard data={item} isActive={index === activeIndex} />
            ) : (
              <div className="h-full w-full bg-black" />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}
